import { createSlice } from '@reduxjs/toolkit';
import absWebService from 'app/services/absWebService';
import i18next from 'i18next';
import { showMessage } from 'app/store/abs/messageSlice';

function mapDbRowToNotification(row) {
  return {
    id: String(row.Notification_ID),
    module: row.Module_short || (row.Module || 'abs').toLowerCase().split('_')[0],
    importance: row.Importance || 'normal',
    title: row.Notification_header || '',
    description: row.Notification_text || '',
    link: row.Notification_link || null,
    time: row.Created_at,
    read: !!row.Acknowledged_at,
  };
}

export const communicateWithAPI = (apicall, data) => async dispatch => {
  const response = await absWebService
    .communicationHandler(apicall, data)
    .catch(error => {
      if (error == null) {
        error = i18next.t('abswebservice:UNKNOWNERROR');
      }
      return error;
    });

  const respdata = await response;

  if (absWebService.isResponseArrSuccess(respdata)) {
    switch (apicall) {
      case 'abs_get_notifications': {
        const rows = JSON.parse(absWebService.getDataFromResponseArr(respdata));
        dispatch(setNotifications(Array.isArray(rows) ? rows.map(mapDbRowToNotification) : []));
        break;
      }
      case 'abs_dismiss_notification': {
        if (data?.notification_id === null) {
          dispatch(removeAllNotifications());
        } else {
          dispatch(removeNotification(String(data?.notification_id)));
        }
        break;
      }
      case 'abs_acknowledge_notification': {
        if (data?.notification_id === null) {
          dispatch(markAllNotificationsRead());
        } else {
          dispatch(markNotificationRead(String(data?.notification_id)));
        }
        break;
      }
      case 'abs_delete_push_device':
      case 'abs_set_push_device_enabled':
        return JSON.parse(absWebService.getDataFromResponseArr(respdata));
      case 'abs_send_test_push':
        return 'ok';
      default:
        break;
    }
  } else {
    const errormessage = absWebService.getDisplayTextFromResponseArr(respdata);
    dispatch(showMessage({ message: errormessage, variant: 'error' }));
  }
};

const initialState = {
  notifications: [],
  knownUnreadCount: null,
  dismissingAll: false,
};

const notificationSlice = createSlice({
  name: 'notificationPanel/data',
  initialState,
  reducers: {
    setNotifications: (state, action) => {
      state.notifications = action.payload;
    },
    addNotification: (state, action) => {
      state.notifications.unshift(action.payload);
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    removeAllNotifications: (state) => {
      state.notifications = [];
    },
    markNotificationRead: (state, action) => {
      const n = state.notifications.find(n => n.id === action.payload);
      if (n) n.read = true;
    },
    markAllNotificationsRead: (state) => {
      state.notifications.forEach(n => { n.read = true; });
    },
    setKnownUnreadCount: (state, action) => {
      state.knownUnreadCount = action.payload;
    },
    setDismissingAll: (state, action) => {
      state.dismissingAll = action.payload;
    },
  },
});

export const {
  setNotifications,
  addNotification,
  removeNotification,
  removeAllNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  setKnownUnreadCount,
  setDismissingAll,
} = notificationSlice.actions;

// Thunk: called whenever the server reports a new unread count via nextkeyiv.
// Fetches notifications only when the count differs from what we last saw.
export const handleUnreadCountUpdate = (newCount) => (dispatch, getState) => {
  const { knownUnreadCount } = getState().notificationPanel.data;
  if (newCount !== knownUnreadCount) {
    dispatch(setKnownUnreadCount(newCount));
    dispatch(communicateWithAPI('abs_get_notifications', null));
  }
};

const IMPORTANCE_ORDER = { highest: 6, higher: 5, high: 4, normal: 3, low: 2, lower: 1, lowest: 0 };

export const selectNotifications = ({ notificationPanel }) => {
  const notifications = notificationPanel.data.notifications;
  return [...notifications].sort((a, b) => {
    const impDiff = (IMPORTANCE_ORDER[b.importance] ?? 3) - (IMPORTANCE_ORDER[a.importance] ?? 3);
    if (impDiff !== 0) return impDiff;
    return new Date(b.time) - new Date(a.time);
  });
};

export const selectKnownUnreadCount = ({ notificationPanel }) => notificationPanel.data.knownUnreadCount;
export const selectDismissingAll = ({ notificationPanel }) => notificationPanel.data.dismissingAll;

export default notificationSlice.reducer;

